import { useEffect, useSyncExternalStore } from "react";
import { getUnreadCount } from "@/api/notifications";

/** Single shared poll across every consumer. Each mount of
 *  useNotifications subscribes to the singleton instead of starting
 *  its own interval, so the Header bell + the Notifications page
 *  don't double-poll while both are mounted. Cadence matches the
 *  "refetchInterval: 60_000" spec; the interval no-ops when the tab
 *  is hidden so a backgrounded window stops hitting the API. */
const POLL_INTERVAL_MS = 60_000;
/** When a new consumer mounts within this window of the last fetch,
 *  reuse the cached count instead of firing a fresh request. Keeps
 *  the bell + page mount sequence to a single network call. */
const STALE_MS = 30_000;

let currentCount = 0;
let lastFetchMs = 0;
let pendingFetch: Promise<void> | null = null;
let intervalHandle: ReturnType<typeof setInterval> | null = null;
let activeConsumers = 0;

const subscribers = new Set<() => void>();

function notify() {
  subscribers.forEach((cb) => cb());
}

function setShared(next: number) {
  if (currentCount === next) return;
  currentCount = next;
  notify();
}

/** De-duped fetcher. Concurrent callers (e.g. an optimistic update
 *  firing refetch() while the interval also ticks) share the same
 *  in-flight promise so the backend never sees two simultaneous
 *  unread-count requests from the same tab. */
async function fetchUnreadCount(): Promise<void> {
  if (pendingFetch) return pendingFetch;
  pendingFetch = (async () => {
    try {
      const res = await getUnreadCount();
      setShared(res.count);
      lastFetchMs = Date.now();
    } catch {
      // Silent — the auth interceptor handles 401, anything else is
      // non-critical bell metadata.
    } finally {
      pendingFetch = null;
    }
  })();
  return pendingFetch;
}

function startPolling() {
  if (intervalHandle) return;
  intervalHandle = setInterval(() => {
    // No refetchOnWindowFocus — and no-op when the tab is hidden so
    // a backgrounded session stops generating traffic. The next
    // foregrounded interval tick (within ≤60s) catches up.
    if (
      typeof document !== "undefined" &&
      document.visibilityState !== "visible"
    ) {
      return;
    }
    void fetchUnreadCount();
  }, POLL_INTERVAL_MS);
}

function stopPolling() {
  if (!intervalHandle) return;
  clearInterval(intervalHandle);
  intervalHandle = null;
}

function subscribe(cb: () => void): () => void {
  subscribers.add(cb);
  return () => {
    subscribers.delete(cb);
  };
}

function getSnapshot(): number {
  return currentCount;
}

/** Stable mutator exposed to consumers — used for optimistic
 *  updates (e.g. setUnreadCount((c) => c - 1) on mark-as-read).
 *  Module-level so the reference never changes across renders. */
function setUnreadCount(
  next: number | ((prev: number) => number),
): void {
  const value =
    typeof next === "function"
      ? (next as (prev: number) => number)(currentCount)
      : next;
  setShared(Math.max(0, value));
}

/**
 * Subscribes the component to the shared unread-count state.
 *
 * - First consumer to mount fires an immediate fetch (unless the
 *   cached value is still fresh) and starts the 60s interval.
 * - Subsequent mounts within STALE_MS reuse the cache — useful when
 *   the bell stays mounted and the notifications page mounts on top.
 * - Last consumer to unmount stops the interval; the cached count
 *   stays so a quick remount doesn't re-fire the network.
 */
export function useNotifications() {
  const unreadCount = useSyncExternalStore(subscribe, getSnapshot);

  useEffect(() => {
    activeConsumers += 1;
    if (Date.now() - lastFetchMs > STALE_MS) void fetchUnreadCount();
    startPolling();
    return () => {
      activeConsumers -= 1;
      if (activeConsumers === 0) stopPolling();
    };
  }, []);

  return { unreadCount, setUnreadCount, refetch: fetchUnreadCount };
}
